import { Component, Host, h, State, Element, Prop, Build } from '@stencil/core';
import Cookies from 'cookies-ts';

interface BannerData {
    id: number
    image: string
    link: string
    validFrom: string
    validTo: string
    expires: string
    attribute: {
        zrherobannerTheme: string
        zrherobannerTitle: string
        zrherobannerContent: string
        zrherobannerBannerVoucher: string
        zrherobannerBannerFunction: 'voucherCode' | 'CTA' | 'Voucher Code' | 'none'
        zrherobannerShowCountdown: number
        zrherobannerCountdown: string
    }
}

@Component({
    tag: 'wf-countdown-banner',
    styleUrl: 'wf-countdown-banner.scss',
    shadow: true,
})
// eslint-disable-next-line require-jsdoc
export class WfCountdownBanner {

    // Host element
    @Element() private host: HTMLElement;

    // State variable for the banner data storage key
    @State() private cookieNameHeroBanner = 'heroBanner';

    // State variable for the cookie name
    @State() private cookieNameBannerClosed = 'wf-banner-closed';

    // State variable for the copy success
    @State() private copySuccess = false;

    // State variable for the banner data
    @State() private bannerData: null | BannerData = null;

    // Countdown interval
    @Prop() countdownInterval = 100;

    // Show banner again after days
    @Prop() showBannerAgainAfterDays = 1;

    /**
     * @inheritdoc
     */
    componentDidLoad() {
        this.init();
    }

    /**
     * @inheritdoc
     */
    render() {
        const showCountdown = this.bannerData?.attribute?.zrherobannerShowCountdown === 1
        const showVoucher = Boolean(
            (this.bannerData?.attribute?.zrherobannerBannerFunction === 'voucherCode' || 
                this.bannerData?.attribute?.zrherobannerBannerFunction === 'Voucher Code') &&
            this.bannerData?.attribute?.zrherobannerBannerVoucher)

        return (
            <Host
                style={{ display: this.bannerData ? 'block' : 'none' }}
            >
                <div
                    class={`countdown-container ${!showVoucher && !showCountdown ? 'jc-center' : ''}`}
                    id="countdown-container"
                >
                    {showCountdown && (
                        <div class="countdown">
                            <div class="countdown-item">
                                <vx-icon class="vx-icon" icon="timer" invert></vx-icon>
                            </div>
                            <div class="countdown-item">
                                <span class="countdown-number" id="days">
                                    {this.getCountDownDates('day')}
                                </span>
                            </div>
                            <div class="countdown-item">
                                <span
                                    class="countdown-number countdown-number-divider"
                                    id="hours">{this.getCountDownDates('hour')}
                                </span>
                            </div>
                            <div class="countdown-item">
                                <span
                                    class="countdown-number countdown-number-divider"
                                    id="minutes">{this.getCountDownDates('minutes')}
                                </span>
                            </div>
                            <div class="countdown-item">
                                <span
                                    class="countdown-number countdown-number-divider"
                                    id="seconds">{this.getCountDownDates('seconds')}
                                </span>
                            </div>
                        </div>
                    )}
                    <div class={`headlines ${!showVoucher && showCountdown ? 'mr-48' : ''}`}>
                        <div class="headline">{this.bannerData?.attribute?.zrherobannerTitle}</div>
                        <div class="sub-headline">{this.bannerData?.attribute?.zrherobannerContent}</div>
                        {this.bannerData?.link && (
                            <div class="cta-button">
                                <a
                                    class="cta-button-link"
                                    href={this.bannerData?.link}
                                    title={this.bannerData?.attribute?.zrherobannerTitle} >
                                    {this.bannerData?.attribute?.zrherobannerBannerFunction === 'CTA'
                                        ? 'Jetzt entdecken'
                                        : 'Mehr erfahren'
                                    }
                                </a>
                            </div>
                        )}
                    </div>
                    {showVoucher && (
                        <div class="voucher-field" onClick={this.copyToClipboardListener}>
                            <div class="voucher-code">
                                <div class="voucher-label">Code: </div>
                                <div class="code" id="voucherCode">{this.bannerData?.attribute?.zrherobannerBannerVoucher}</div>
                            </div>
                            <div class="copy-button" id="copyButton">
                                {this.copySuccess
                                    ? <vx-icon class="vx-icon" icon="done" invert></vx-icon>
                                    : <vx-icon class="vx-icon" icon="content_copy" invert></vx-icon>
                                }

                            </div>
                        </div>
                    )}
                    <div class="close-button">
                        <button id="closeButton" onClick={this.onCloseButtonClicked}>
                            x
                        </button>
                    </div>
                </div>
            </Host>
        );
    }

    /**
     * Convert the date string to a valid date     .
     * @param {string} date - The date string.
     * @returns {Date} - The valid date.
     */
    private convertToValidDate = (date: string) => {
        // Replace '/' with '-' to match the ISO 8601 extended format
        return new Date(date.replace(/\//g, '-'))
    }

    /**
     * Get the countdown dates.
     * @param {string} target - The target date.
     * @returns {string} - The countdown dates.
     */
    private getCountDownDates = (target: 'day' | 'hour' | 'minutes' | 'seconds'): string => {
        if (!this.bannerData || !this.bannerData.attribute) return '';

        const { zrherobannerCountdown, zrherobannerShowCountdown } = this.bannerData.attribute;
        if (!zrherobannerCountdown) return '';

        if (!zrherobannerShowCountdown || zrherobannerShowCountdown === 0) return '';

        const targetDate = this.convertToValidDate(zrherobannerCountdown);
        const now = new Date();

        const diff = targetDate.getTime() - now.getTime();

        let countdownValue;
        if (target === 'day') countdownValue = Math.floor(diff / (1000 * 60 * 60 * 24));
        if (target === 'hour') countdownValue = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        if (target === 'minutes') countdownValue = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        if (target === 'seconds') countdownValue = Math.floor((diff % (1000 * 60)) / 1000);
        if (countdownValue) return String(countdownValue).padStart(2, '0');
        if (countdownValue === 0) return '00';

        return '';
    }

    /**
     * Copy the voucher code to the clipboard.
     */
    private copyToClipboardListener = (e) => {
        e.preventDefault();
        navigator.clipboard.writeText(this.bannerData?.attribute?.zrherobannerBannerVoucher)
        const copyButton = this.host.shadowRoot.querySelector('#copyButton');
        if (copyButton) {
            this.copySuccess = true;
            setTimeout(() => {
                this.copySuccess = false;
            }, 1500)
        }
    }

    /**
     * Update the countdown.
     */
    private updateCountdown = (): void => {
        try {
            const daysEl: HTMLElement | null = this.host.shadowRoot.querySelector('#days');
            const hoursEl: HTMLElement | null = this.host.shadowRoot.querySelector('#hours');
            const minutesEl: HTMLElement | null = this.host.shadowRoot.querySelector('#minutes');
            const secondsEl: HTMLElement | null = this.host.shadowRoot.querySelector('#seconds');

            if (!daysEl || !hoursEl || !minutesEl || !secondsEl) {
                return;
            }

            daysEl.textContent = String(this.getCountDownDates('day'))
            hoursEl.textContent = String(this.getCountDownDates('hour'))
            minutesEl.textContent = String(this.getCountDownDates('minutes'))
            secondsEl.textContent = String(this.getCountDownDates('seconds'))

        } catch (error) {
            console.error('Error in updateCountdown: ', error);
        }
    }

    /**
     * Close the banner and set a cookie.
     */
    private onCloseButtonClicked = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const date = new Date();
        date.setTime(date.getTime() + (this.showBannerAgainAfterDays * 24 * 60 * 60 * 1000));

        const cookies = new Cookies();
        cookies.set(this.cookieNameBannerClosed, '1', { expires: date });

        this.bannerData = null;
    }

    /**
     * Fetch the banner data.
     * @returns {Promise<BannerData | null>} - The banner data.
     */
    private fetchData = async (): Promise<BannerData | null> => {
        try {
            // Get the CSRF token - if we do not have one return early
            const csrfToken = window.CSRF?.getToken()
            if (!csrfToken) return null;

            // Fetch the banner data - the BE Controller will set the cookie with the banner data
            const heroBanner = await fetch('/ZRHeroBanner/heroBannerJson', {
                method: 'POST',
                headers: {
                    'x-csrf-token': csrfToken,
                    'x-requested-with': 'XMLHttpRequest',
                },
            })
                .then((response) => {
                    if (response.ok) {
                        return response.json();
                    }
                    throw new Error('Network response was not ok.');
                })
                .catch((error) => {
                    console.error('Error when fetching hero banner: ', error);
                });

            if (!heroBanner || !heroBanner.id) return null;

            // add the current timestamp plus 10 minutes to the expires property
            heroBanner.expires = new Date(new Date().getTime() + 10 * 60 * 1000).toISOString();

            // Save the banner data to session storage
            sessionStorage.setItem('heroBanner', JSON.stringify(heroBanner));

            return heroBanner;
        } catch (error) {
            console.error('Error when fetching hero banner: ', error);
            return null;
        }
    }

    /**
     * Initialize the component.
     * @returns {null} Void.
     */
    private init = async () => {
        try {
            if (Build.isBrowser) {
                const cookies = new Cookies()

                // If the user closed the banner, do not show it again
                const bannerClosed = cookies.get(this.cookieNameBannerClosed);
                if (bannerClosed) return null;

                let heroBanner = null;
                const heroBannerString = sessionStorage.getItem(this.cookieNameHeroBanner);
                if (heroBannerString) {
                    heroBanner = JSON.parse(heroBannerString);
                }

                // If we still do not have a banner, try to fetch it
                if (!heroBanner || !heroBanner.id) {
                    heroBanner = await this.fetchData();
                }

                // If we have a banner and the expires property is set, check if the banner is still valid
                if (heroBanner && heroBanner.expires) {
                    const expires = new Date(heroBanner.expires);
                    const now = new Date();
                    if (now > expires) {
                        heroBanner = await this.fetchData();
                    }
                }

                // If we still do not have a banner, return early
                // Set the cookie to not show the banner again to prevent fetching the banner data again
                if (!heroBanner || !heroBanner.id) {
                    const date = new Date();
                    const tenMinutes = 10 * 60 * 1000;
                    date.setTime(date.getTime() + tenMinutes);
                    cookies.set(this.cookieNameBannerClosed, '1', { expires: date });
                    return null;
                }

                const { validFrom, validTo } = heroBanner as unknown as BannerData;

                const validFromDate = this.convertToValidDate(validFrom);
                const validToDate = this.convertToValidDate(validTo);
                const now = new Date();

                // If the banner is not active, return early
                if (!(now > validFromDate && now < validToDate)) {
                    return;
                }

                // If the banner is active, set the banner data - this will display the banner
                this.bannerData = heroBanner as unknown as BannerData;

                setInterval(() => {
                    this.updateCountdown();
                }, this.countdownInterval);
            }
        } catch (error) {
            console.error('Error when creating hero banner: ', error);
        }
    }
}

